import axios from 'axios';
import { apiClient } from './api';

/**
 * Homepage photo limits — mirror HOMEPAGE_MAX_FILE_SIZE and ALLOWED_MIME_TYPES
 * in backend/src/middleware/upload.ts (issue #153).
 */
export const HOMEPAGE_IMAGE_MAX_MB = 8;
export const HOMEPAGE_IMAGE_ACCEPT = '.jpg,.jpeg,.png,image/jpeg,image/png';
const HOMEPAGE_IMAGE_TYPES = ['image/jpeg', 'image/png'];

/**
 * Why a homepage photo can't be uploaded, or null when it can. Checked before
 * posting so staff see the limit instead of a generic HTTP error.
 */
export function validateHomepageImage(file: File): string | null {
  if (!HOMEPAGE_IMAGE_TYPES.includes(file.type)) {
    return 'Please choose a JPEG or PNG image.';
  }
  if (file.size > HOMEPAGE_IMAGE_MAX_MB * 1024 * 1024) {
    const sizeMb = (file.size / (1024 * 1024)).toFixed(1);
    return `This image is ${sizeMb} MB. Please choose one under ${HOMEPAGE_IMAGE_MAX_MB} MB.`;
  }
  return null;
}

export interface DashboardAnalytics {
  summary: {
    totalUsers: number;
    totalProducts: number;
    totalQuotations: number;
  };
  userStats: Record<string, number>;
  quotationStats: Record<string, number>;
  recentQuotations: Array<{
    RefNo: string;
    TraderName: string;
    webEmail: string;
    DateIssued: string;
    TotalDueAmt: number;
    webStatus: string;
    webUserID: string;
  }>;
}

export interface InactiveCustomer {
  traderCode: string;
  traderName: string;
  contact: string;
  phone: string;
  email: string;
  priceLevel: string;
  webGroup: string;
  lastActivity: string | null;
  daysInactive: number | null;
  sales12mo: number | null;
  source: 'erp' | 'web' | 'none';
}

export interface CustomerOrder {
  RefNo: string;
  DateIssued: string | null;
  TotalDueAmt: number;
  webStatus: string;
  PromoCode: string | null;
  DiscountAmt: number | null;
  ItemsSummary: string | null;
}

export interface GuestCustomer {
  email: string;
  name: string;
  orderCount: number;
  lastOrderDate: string | null;
  totalAmount: number;
}

export interface InactiveCustomerReport {
  buckets: {
    total: number;
    active: number;
    inactive30: number;
    inactive60: number;
    inactive90: number;
    never: number;
    fromErpFeed: number;
  };
  customers: InactiveCustomer[];
}

export interface ProductCategoryOption {
  Category: string;
  CategoryDescription: string;
}

export interface ProductPayload {
  SupersedeNo: string;
  UOM: string;
  Description: string;
  ProductDetail?: string;
  Category?: string;
  Price?: number;
  ConvRate?: number;
  NetContentLabel?: string;
  DeliveryCalUnit?: number;
  Active?: boolean;
  MainPictureName?: string;
}

export interface ProductUpdatePayload extends Partial<ProductPayload> {}

export interface User {
  UserID: string;
  UserName: string;
  UserType: string;
  UserEmail: string;
  HandPhone: string;
  TraderCode: string;
  DateEntry: string;
  LastLogInDate: string;
  Super: string;
}

export interface PaginatedResponse<T> {
  data: T[];
  pagination: {
    currentPage: number;
    totalPages: number;
    totalItems: number;
    itemsPerPage: number;
  };
}

export type QuotationStatus =
  | 'pending'
  | 'processing'
  | 'shipped'
  | 'completed'
  | 'cancelled';

export interface Quotation {
  RefNo: string;
  TraderName: string;
  webEmail: string;
  DateIssued: string;
  TotalDueAmt: number;
  GSTAmt: number;
  webStatus: string;
  webUserID: string;
  webTraderCode: string;
  webDateEntry: string;
  TrackingNumber?: string | null;
  DeliveryCompanyName?: string | null;
  ShippedAt?: string | null;
  StatusChangedAt?: string | null;
  PaymentMethod?: OrderPaymentMethod | null;
  // PaymentMethod records intent at checkout; PaymentStatus is what means the
  // money actually arrived (a wallet debit can fail after the order is created).
  PaymentStatus?: string | null;
  RefundStatus?: CardRefundStatus | null;
  RefundAmount?: number | null;
  // Refund timing, and Razorpay's own words when a refund was rejected, so a
  // red "Refund failed" tag can say what went wrong (issues #133/#134).
  RefundInitiatedAt?: string | null;
  RefundedAt?: string | null;
  RefundFailureReason?: string | null;
  // A failed refund repaid by bank transfer: staff's remark and who recorded
  // it; RefundedAt holds when (issue #171).
  RefundRemark?: string | null;
  RefundMarkedBy?: string | null;
  // Only present while a refund is in flight: when the money should land.
  RefundExpected?: RefundWindow | null;
  // When the order record last changed — status, refund, payment received or
  // an admin edit (issue #181). Falls back to the entry/issue date server-side.
  LastUpdatedAt?: string | null;
  // Who made the last recorded change: the admin on the newest audit-trail
  // row, 'customer' for a payment received, 'system' for a gateway refund
  // event, NULL for an order untouched since creation.
  LastUpdatedBy?: string | null;
}

/** When a submitted refund should reach the customer (issue #134). */
export interface RefundWindow {
  earliest: string;
  latest: string;
  workingDaysMin: number;
  workingDaysMax: number;
}

export interface QuotationStatusHistoryEntry {
  HistoryID: string;
  RefNo: string;
  FromStatus: string | null;
  ToStatus: string;
  Notes: string | null;
  NotesEmailedToCustomer: boolean;
  ChangedBy: string | null;
  ChangedAt: string;
}

export interface QuotationDetail extends Quotation {
  items: Array<{
    RefNo: string;
    LineNO: number;
    SupersedeNo: string;
    UOM: string;
    Description: string;
    Qty: number;
    Price: number;
    ItemNetAmt: number;
  }>;
  // Gross (goods + delivery), promo discount, and applied code — present when the
  // order used a promo code (DiscountAmt > 0 / PromoCode set).
  GrossGoodsAmt?: number;
  DiscountAmt?: number;
  PromoCode?: string | null;
  // Delivery details captured at checkout. Legacy rows may have these empty or
  // missing entirely, so every consumer must handle the absent case.
  webDelName?: string | null;
  webAddress1?: string | null;
  webAddress2?: string | null;
  webAddress3?: string | null;
  webTel?: string | null;
  statusHistory?: QuotationStatusHistoryEntry[];
}

export type OrderPaymentMethod = 'card' | 'wallet' | 'none';

// 'manual': the refund failed and the customer was repaid by bank transfer (issue #171).
export type CardRefundStatus = 'none' | 'pending' | 'processed' | 'failed' | 'manual';

/** Why an order cannot be refunded automatically on cancel. */
export type RefundBlockedReason =
  | 'not_paid'
  | 'legacy_unlinked'
  | 'already_refunded'
  | 'refund_pending'
  | 'partially_refunded'
  | 'payment_unreadable'
  | 'refund_unverified'
  | 'refunded_manually'
  | 'razorpay_not_configured';

export interface QuotationRefundInfo {
  // Wallet fields, unchanged — an older frontend still reads these.
  walletPaid: boolean;
  amount: number;
  alreadyRefunded: boolean;
  // Card / combined preview.
  paymentMethod: OrderPaymentMethod;
  cardPaid: boolean;
  /** Will cancelling fire an automatic refund? */
  refundable: boolean;
  blockedReason?: RefundBlockedReason;
  cardRefundStatus: CardRefundStatus;
  currency: string;
  orderStatus: string;
}

export interface QuotationRefundResult {
  refunded: boolean;
  amount: number;
  alreadyRefunded: boolean;
}

export interface CardRefundResult {
  channel: 'card';
  status: 'initiated' | 'processed' | 'skipped';
  reason?: RefundBlockedReason;
  amount: number;
  refundId?: string;
}

export interface QuotationStatusUpdateResult {
  message: string;
  refund?: QuotationRefundResult;
  cardRefund?: CardRefundResult;
  refundFailed: boolean;
}

export interface DeliveryCompany {
  DeliveryCompanyID: string;
  Name: string;
  IsActive: boolean;
  SortOrder: number;
  CreatedAt: string;
  UpdatedAt: string;
}

export interface SystemSettings {
  razorpay: {
    key_id: string | null;
    key_secret: string | null;
    razorpay_webhook_secret: string | null;
  };
  email: {
    smtp_host: string | null;
    smtp_port: string | null;
    smtp_user: string | null;
    smtp_password: string | null;
    from_email: string | null;
    from_name: string | null;
  };
  general: {
    site_name: string | null;
    admin_email: string | null;
    order_notification_recipients: string | null;
    gst_rate: string | null;
    currency: string | null;
    min_order_amount: string | null;
    delivery_charge: string | null;
  };
  features: {
    allow_guest_checkout: string | null;
    require_email_verification: string | null;
    maintenance_mode: string | null;
  };
  terms?: {
    text?: string | null;
    topup_text?: string | null;
  };
  reports?: {
    inactive_report_enabled?: string | null;
    inactive_report_day?: string | null;
    inactive_report_hour?: string | null;
  };
  // Homepage content staff can edit without a deploy (issue #132).
  homepage?: {
    hero_headline?: string | null;
    hero_subtext?: string | null;
    hero_image?: string | null;
    promo_enabled?: string | null;
    promo_text?: string | null;
    // Hero background and the sections below it (issue #140).
    hero_bg_color?: string | null;
    hero_bg_image?: string | null;
    hero_bg_position?: string | null;
    hero_bg_zoom?: string | null;
    features_title?: string | null;
    features_subtitle?: string | null;
    features_bg_color?: string | null;
    feature1_icon?: string | null;
    feature1_title?: string | null;
    feature1_text?: string | null;
    feature2_icon?: string | null;
    feature2_title?: string | null;
    feature2_text?: string | null;
    feature3_icon?: string | null;
    feature3_title?: string | null;
    feature3_text?: string | null;
    cta_title?: string | null;
    cta_text?: string | null;
    cta_button?: string | null;
    cta_bg_color?: string | null;
  };
}

/** What the backend returns after saving an uploaded homepage image. */
export interface HomepageUploadResult {
  path: string;
  /** The background photo's saved focus point (issue #154). */
  position?: string;
  /** The background photo's saved zoom in percent (issue #162). */
  zoom?: number;
  width: number;
  height: number;
  size: number;
}

export interface EmailTemplate {
  StatusKey: string;
  Subject: string;
  BodyHtml: string;
  Enabled: boolean;
  UpdatedBy: string | null;
  UpdatedAt: string;
}

export interface EmailTemplateInput {
  subject: string;
  bodyHtml: string;
  enabled: boolean;
}

export interface EmailTemplateVariable {
  token: string;
  description: string;
}

export interface UserDetail extends User {
  traderInfo?: {
    TraderCode: string;
    TraderName: string;
    Address1: string;
    Address2: string;
    Address3: string;
    Tel: string;
    Email: string;
    PriceLevel: string;
    CreditLimit: number;
    PaymentTerm: string;
  };
  recentQuotations: Array<{
    RefNo: string;
    TraderName: string;
    DateIssued: string;
    TotalDueAmt: number;
    GSTAmt: number;
    webStatus: string;
    ItemsSummary?: string | null;
  }>;
}

export interface CreateUserPayload {
  userId: string;
  userName: string;
  password: string;
  userType: 'ADMIN' | 'USER';
  userEmail: string;
  handPhone?: string;
  traderCode?: string;
}

class AdminService {
  /**
   * Get dashboard analytics
   */
  async getDashboardAnalytics(): Promise<DashboardAnalytics> {
    const response = await apiClient.get<DashboardAnalytics>('/admin/dashboard');
    return response.data!;
  }

  /**
   * Inactive-customer dashboard: full customer master with days-inactive +
   * 30/60/90+/never bucket counts.
   */
  async getInactiveCustomers(): Promise<InactiveCustomerReport> {
    const response = await apiClient.get<InactiveCustomerReport>('/admin/customers/inactive');
    return response.data!;
  }

  /** Email the inactive-customer report (summary + list) to the admin recipients. */
  async emailInactiveCustomers(): Promise<{ recipients: string[]; message: string }> {
    const response = await apiClient.post<{ recipients: string[] }>('/admin/customers/inactive/email');
    return { recipients: response.data?.recipients ?? [], message: response.message ?? 'Report emailed' };
  }

  /** A single customer's web-order history (by TraderCode), with item summaries. */
  async getCustomerOrders(traderCode: string): Promise<CustomerOrder[]> {
    const response = await apiClient.get<CustomerOrder[]>(
      `/admin/customers/${encodeURIComponent(traderCode)}/orders`
    );
    return response.data ?? [];
  }

  /** Guest customers: guest orders grouped by email (not yet a registered account). */
  async getGuestCustomers(): Promise<GuestCustomer[]> {
    const response = await apiClient.get<GuestCustomer[]>('/admin/guests');
    return response.data ?? [];
  }

  /**
   * Convert a guest into a registered (verified) account and link their orders.
   * No password is set here — the customer is emailed a link to choose their own.
   * `emailSent` false means the account exists but nobody has told the customer.
   */
  async convertGuest(input: { email: string; name: string; traderCode?: string }):
    Promise<{ email: string; claimedOrders: number; emailSent: boolean; message: string }> {
    const response = await apiClient.post<{ email: string; claimedOrders: number; emailSent?: boolean }>(
      '/admin/guests/convert',
      input
    );
    return {
      email: response.data?.email ?? input.email,
      claimedOrders: response.data?.claimedOrders ?? 0,
      emailSent: response.data?.emailSent === true,
      message: response.message ?? 'Guest converted',
    };
  }

  /**
   * Get all users with pagination
   */
  async getUsers(params: {
    page?: number;
    limit?: number;
    search?: string;
    userType?: string;
  } = {}): Promise<PaginatedResponse<User>> {
    const response = await apiClient.get<{
      users: User[];
      pagination: {
        currentPage: number;
        totalPages: number;
        totalItems: number;
        itemsPerPage: number;
      };
    }>('/admin/users', params);
    return {
      data: response.data!.users,
      pagination: response.data!.pagination
    };
  }

  /**
   * Get user details
   */
  async getUserDetails(userId: string): Promise<UserDetail> {
    const response = await apiClient.get<{
      user: User;
      traderInfo: UserDetail['traderInfo'];
      recentQuotations: UserDetail['recentQuotations'];
    }>(`/admin/users/${userId}`);
    // Flatten the response: spread user data at top level, add traderInfo and recentQuotations
    // Backend returns { success, message, data: { user, traderInfo, recentQuotations } }
    const { user, traderInfo, recentQuotations } = response.data!;
    return {
      ...user,
      traderInfo,
      recentQuotations
    };
  }

  /**
   * Create a new user
   */
  async createUser(userData: CreateUserPayload): Promise<User> {
    const response = await apiClient.post<User>('/admin/users', userData);
    return response.data!;
  }

  /**
   * Update user status
   */
  async updateUserStatus(userId: string, status: 'active' | 'inactive', reason?: string): Promise<void> {
    await apiClient.put(`/admin/users/${userId}/status`, { status, reason });
  }

  /**
   * Update a customer's own details — name, email, phone (issue #100).
   *
   * Only the changed fields are sent; the UserID is not editable, since it is
   * the login identity and what the customer's orders are keyed to. Returns the
   * message so the caller can surface the email-verification note.
   */
  async updateUserProfile(userId: string, updates: {
    name?: string;
    email?: string;
    phone?: string;
  }): Promise<{ user: User; message: string }> {
    const response = await apiClient.put<User>(`/admin/users/${userId}/profile`, updates);
    return { user: response.data!, message: response.message || 'Customer details updated' };
  }

  /**
   * Update user's trader/trading information
   */
  async updateTraderInfo(userId: string, traderData: {
    TraderName?: string;
    Address1?: string;
    Address2?: string;
    Address3?: string;
    Tel?: string;
    PriceLevel?: string;
    CreditLimit?: number;
    PaymentTerm?: string;
  }): Promise<UserDetail['traderInfo']> {
    const response = await apiClient.put<{
      TraderCode: string;
      TraderName: string;
      Address1: string;
      Address2: string;
      Address3: string;
      Tel: string;
      Email: string;
      PriceLevel: string;
      CreditLimit: number;
      PaymentTerm: string;
    }>(`/admin/users/${userId}/trader`, traderData);
    return response.data!;
  }

  /**
   * Get user's quotations with pagination
   */
  async getUserQuotations(userId: string, params: {
    page?: number;
    limit?: number;
  } = {}): Promise<{
    quotations: UserDetail['recentQuotations'];
    pagination: {
      currentPage: number;
      totalPages: number;
      totalItems: number;
      itemsPerPage: number;
    };
  }> {
    const response = await apiClient.get<{
      quotations: UserDetail['recentQuotations'];
      pagination: {
        currentPage: number;
        totalPages: number;
        totalItems: number;
        itemsPerPage: number;
      };
    }>(`/admin/users/${userId}/quotations`, params);
    return {
      quotations: response.data!.quotations,
      pagination: response.data!.pagination
    };
  }

  /**
   * Get all quotations with pagination
   */
  async getQuotations(params: {
    page?: number;
    limit?: number;
    search?: string;
    status?: string;
    /** 'failed': only orders whose card refund failed (issue #171). */
    refund?: 'failed';
    startDate?: string;
    endDate?: string;
    /** Last-updated range on the order record (issue #181). */
    updatedStart?: string;
    updatedEnd?: string;
    sortBy?: 'statusAge' | 'amount';
    sortOrder?: 'asc' | 'desc';
  } = {}): Promise<PaginatedResponse<Quotation> & { statusCounts: Record<string, number> }> {
    const response = await apiClient.get<{
      quotations: Quotation[];
      statusCounts?: Record<string, number>;
      pagination: {
        currentPage: number;
        totalPages: number;
        totalItems: number;
        itemsPerPage: number;
      };
    }>('/admin/quotations', params);
    return {
      data: response.data!.quotations,
      statusCounts: response.data!.statusCounts ?? {},
      pagination: response.data!.pagination
    };
  }

  /**
   * Export orders as CSV, one row per order line, including delivery address
   * and item detail. The server builds the file, so this is a single request
   * rather than paging the list endpoint and fetching items per order.
   *
   * Omit `limit` to export everything matching the filters; the dashboard's
   * recent-orders card passes 10 to match the rows on screen.
   */
  async exportOrdersCSV(params: {
    search?: string;
    status?: string;
    /** 'failed': only orders whose card refund failed (issue #171). */
    refund?: 'failed';
    startDate?: string;
    endDate?: string;
    /** Last-updated range on the order record (issue #181). */
    updatedStart?: string;
    updatedEnd?: string;
    sortBy?: 'statusAge' | 'amount';
    sortOrder?: 'asc' | 'desc';
    limit?: number;
  } = {}): Promise<Blob> {
    const query = Object.fromEntries(
      Object.entries(params).filter(([, v]) => v !== '' && v !== null && v !== undefined)
    );
    return apiClient.getBlob('/admin/quotations/export', query);
  }

  /**
   * Get quotation details
   */
  async getQuotationDetails(quotationId: string): Promise<QuotationDetail> {
    const response = await apiClient.get<QuotationDetail>(`/admin/quotations/${quotationId}`);
    return response.data!;
  }

  /**
   * Refund preview for an order. Tells the Cancel dialog how the order was paid,
   * how much would be refunded and to where, and — when no automatic refund is
   * possible — why, so the admin can be told to refund manually instead of
   * cancelling into silence.
   */
  async getQuotationRefundInfo(quotationId: string): Promise<QuotationRefundInfo> {
    const response = await apiClient.get<QuotationRefundInfo>(
      `/admin/quotations/${quotationId}/refund-info`
    );
    return (
      response.data ?? {
        walletPaid: false,
        amount: 0,
        alreadyRefunded: false,
        paymentMethod: 'none',
        cardPaid: false,
        refundable: false,
        cardRefundStatus: 'none',
        currency: 'SGD',
        orderStatus: '',
      }
    );
  }

  /**
   * Update quotation status. When advancing to 'shipped', trackingNumber and
   * deliveryCompanyName are required (server-side enforced too). Returns the
   * server message plus any refund result — `refund` for a wallet-paid order,
   * `cardRefund` for a card-paid one.
   */
  async updateQuotationStatus(
    quotationId: string,
    status: QuotationStatus,
    notes?: string,
    opts?: {
      sendNotesToCustomer?: boolean;
      trackingNumber?: string;
      deliveryCompanyName?: string;
    }
  ): Promise<QuotationStatusUpdateResult> {
    const response = await apiClient.put<{
      refund?: QuotationRefundResult;
      cardRefund?: CardRefundResult;
      refundFailed?: boolean;
    }>(`/admin/quotations/${quotationId}/status`, {
      status,
      notes,
      sendNotesToCustomer: opts?.sendNotesToCustomer,
      trackingNumber: opts?.trackingNumber,
      deliveryCompanyName: opts?.deliveryCompanyName,
    });
    return {
      message: response.message ?? '',
      refund: response.data?.refund,
      cardRefund: response.data?.cardRefund,
      refundFailed: response.data?.refundFailed === true,
    };
  }

  /**
   * Record that a failed card refund was repaid by bank transfer (issue #171).
   * Throws with the server's reason when the order's refund is not `failed`.
   */
  async markRefundedByBankTransfer(quotationId: string, remark: string): Promise<string> {
    const response = await apiClient.post(`/admin/quotations/${quotationId}/manual-refund`, { remark });
    return response.message ?? '';
  }

  /**
   * Send a rejected card refund to Razorpay again (issue #133). Throws with the
   * gateway's reason when it is refused a second time.
   */
  async retryCardRefund(quotationId: string): Promise<{ message: string; result?: CardRefundResult }> {
    const response = await apiClient.post<CardRefundResult>(
      `/admin/quotations/${quotationId}/retry-refund`,
      {}
    );
    return { message: response.message ?? '', result: response.data };
  }

  /**
   * Edit shipping info on a quotation that's already in 'shipped' status.
   * Re-sends the shipping email to the customer with the updated info.
   */
  async updateQuotationShipping(
    quotationId: string,
    trackingNumber: string,
    deliveryCompanyName: string,
    opts?: { sendNotesToCustomer?: boolean; notes?: string }
  ): Promise<void> {
    await apiClient.put(`/admin/quotations/${quotationId}/shipping`, {
      trackingNumber,
      deliveryCompanyName,
      sendNotesToCustomer: opts?.sendNotesToCustomer,
      notes: opts?.notes,
    });
  }

  /**
   * Update quotation email
   */
  async updateQuotationEmail(quotationId: string, email: string): Promise<void> {
    await apiClient.put(`/admin/quotations/${quotationId}/email`, { email });
  }

  // -------- Delivery companies (admin-managed list of couriers) --------

  async getDeliveryCompanies(): Promise<DeliveryCompany[]> {
    const response = await apiClient.get<DeliveryCompany[]>('/admin/delivery-companies');
    return response.data ?? [];
  }

  async getActiveDeliveryCompanies(): Promise<DeliveryCompany[]> {
    const response = await apiClient.get<DeliveryCompany[]>('/admin/delivery-companies/active');
    return response.data ?? [];
  }

  async createDeliveryCompany(input: {
    name: string;
    isActive?: boolean;
    sortOrder?: number;
  }): Promise<DeliveryCompany> {
    const response = await apiClient.post<DeliveryCompany>('/admin/delivery-companies', input);
    return response.data!;
  }

  async updateDeliveryCompany(
    id: string,
    input: { name?: string; isActive?: boolean; sortOrder?: number }
  ): Promise<DeliveryCompany> {
    const response = await apiClient.put<DeliveryCompany>(`/admin/delivery-companies/${id}`, input);
    return response.data!;
  }

  async deleteDeliveryCompany(id: string): Promise<void> {
    await apiClient.delete(`/admin/delivery-companies/${id}`);
  }

  /**
   * Get system settings
   */
  async getSystemSettings(): Promise<SystemSettings> {
    const response = await apiClient.get<SystemSettings>('/admin/settings');
    return response.data!;
  }

  /**
   * Update system settings
   */
  async updateSettings(settings: Partial<SystemSettings>): Promise<SystemSettings> {
    const response = await apiClient.put<SystemSettings>('/admin/settings', settings);
    return response.data!;
  }

  /**
   * Test Razorpay connection
   */
  async testRazorpayConnection(): Promise<{ success: boolean; message: string }> {
    const response = await apiClient.post<unknown>('/admin/settings/test-razorpay');
    return { success: response.success, message: response.message ?? '' };
  }

  /**
   * Test email/SMTP connection
   */
  async testEmailConnection(testEmail?: string): Promise<{ success: boolean; message: string }> {
    // Backend returns a flat { success, message } body (no `data` envelope), so read fields directly off the response.
    const response = await apiClient.post<unknown>('/admin/settings/test-email', { testEmail });
    return { success: response.success, message: response.message ?? '' };
  }

  /**
   * Seed default settings
   */
  async seedDefaultSettings(): Promise<void> {
    await apiClient.post('/admin/settings/seed');
  }

  // -------- Order status email templates (Issue #30) --------

  async getEmailTemplates(): Promise<{
    templates: EmailTemplate[];
    variables: EmailTemplateVariable[];
  }> {
    const response = await apiClient.get<{
      templates: EmailTemplate[];
      variables: EmailTemplateVariable[];
    }>('/admin/email-templates');
    return {
      templates: response.data?.templates ?? [],
      variables: response.data?.variables ?? [],
    };
  }

  async updateEmailTemplate(status: string, input: EmailTemplateInput): Promise<void> {
    await apiClient.put(`/admin/email-templates/${status}`, input);
  }

  async previewEmailTemplate(
    status: string,
    input: { subject: string; bodyHtml: string }
  ): Promise<{ subject: string; html: string }> {
    const response = await apiClient.post<{ subject: string; html: string }>(
      `/admin/email-templates/${status}/preview`,
      input
    );
    return { subject: response.data?.subject ?? '', html: response.data?.html ?? '' };
  }

  // Product Management
  async getProducts(params: {
    page?: number;
    limit?: number;
    search?: string;
    category?: string;
    status?: 'active' | 'inactive' | 'all';
    sortBy?: 'SupersedeNo' | 'Description' | 'Price' | 'Category';
    sortOrder?: 'ASC' | 'DESC';
  } = {}): Promise<{
    products: Product[];
    total: number;
    page: number;
    totalPages: number;
  }> {
    const response = await apiClient.get<{
      products: Product[];
      total: number;
      page: number;
      totalPages: number;
    }>('/admin/products', params);
    return response.data!;
  }

  /**
   * Persist the manual product display order. `order` is the list of product
   * codes (SupersedeNo) in the desired order; index 0 shows first.
   */
  async reorderProducts(order: string[]): Promise<void> {
    await apiClient.put('/admin/products/order', { order });
  }

  async getProductStats(): Promise<{
    totalProducts: number;
    activeProducts: number;
    inactiveProducts: number;
    categories: { category: string; count: number; }[];
  }> {
    const response = await apiClient.get<{
      totalProducts: number;
      activeProducts: number;
      inactiveProducts: number;
      categories: { category: string; count: number; }[];
    }>('/admin/products/stats');
    return response.data!;
  }

  async getProductCategories(): Promise<string[]> {
    const response = await apiClient.get<string[]>('/admin/products/categories');
    return response.data!;
  }

  async getProductDetail(supersedeNo: string, uom: string): Promise<Product> {
    const response = await apiClient.get<Product>(`/admin/products/${supersedeNo}/${uom}`);
    return response.data!;
  }

  async createProduct(productData: ProductPayload): Promise<void> {
    await apiClient.post('/admin/products', productData);
  }

  async updateProduct(supersedeNo: string, uom: string, updateData: ProductUpdatePayload): Promise<void> {
    await apiClient.put(`/admin/products/${supersedeNo}/${uom}`, updateData);
  }

  async deleteProduct(supersedeNo: string, uom: string): Promise<void> {
    await apiClient.delete(`/admin/products/${supersedeNo}/${uom}`);
  }

  async toggleProductStatus(supersedeNo: string, active: boolean): Promise<void> {
    await apiClient.patch(`/admin/products/${supersedeNo}/status`, { active });
  }

  async toggleProductPublish(supersedeNo: string, uom: string, published: boolean): Promise<void> {
    await apiClient.patch(`/admin/products/${supersedeNo}/${uom}/publish`, { published });
  }

  async listProductImages(
    supersedeNo: string,
    uom: string
  ): Promise<Array<{ KeyID: number; SupersedeNo: string; UOM: string; DocName: string; SeqNo: number; path: string }>> {
    const response = await apiClient.get<
      Array<{ KeyID: number; SupersedeNo: string; UOM: string; DocName: string; SeqNo: number; path: string }>
    >(`/admin/products/${encodeURIComponent(supersedeNo)}/${encodeURIComponent(uom)}/images`);
    return response.data ?? [];
  }

  async deleteProductImage(supersedeNo: string, uom: string, keyId: number): Promise<void> {
    await apiClient.delete(
      `/admin/products/${encodeURIComponent(supersedeNo)}/${encodeURIComponent(uom)}/images/${keyId}`
    );
  }

  async moveProductImage(
    supersedeNo: string,
    uom: string,
    keyId: number,
    direction: 'up' | 'down'
  ): Promise<void> {
    await apiClient.patch(
      `/admin/products/${encodeURIComponent(supersedeNo)}/${encodeURIComponent(uom)}/images/${keyId}/move`,
      { direction }
    );
  }

  async uploadProductImage(
    supersedeNo: string,
    uom: string,
    file: File
  ): Promise<{
    KeyID: number;
    SeqNo: number;
    DocName: string;
    path: string;
    width: number;
    height: number;
    size: number;
  }> {
    const formData = new FormData();
    formData.append('image', file);

    // Get auth token from localStorage
    const authData = JSON.parse(localStorage.getItem('dasoonegg-auth') || '{}');
    const token = authData.state?.token;

    // Use axios directly for file upload to avoid Content-Type issues
    const baseUrl = import.meta.env.VITE_API_BASE_URL || '/api/v1';

    try {
      const response = await axios.post(
        `${baseUrl}/admin/products/${supersedeNo}/${uom}/image`,
        formData,
        {
          headers: {
            Authorization: token ? `Bearer ${token}` : '',
          },
          withCredentials: true,
        }
      );

      return response.data.data;
    } catch (error) {
      // Extract meaningful error message from axios error
      if (axios.isAxiosError(error)) {
        const responseData = error.response?.data;
        const errorMessage = responseData?.message
          || responseData?.error?.details
          || error.message
          || 'Image upload failed';

        // Re-throw with extracted message for the UI to display
        throw new Error(errorMessage);
      }
      throw error;
    }
  }

  // Mobile product photo (issue #126). One optional photo per variant, kept
  // outside the numbered gallery; null means the product has none.
  async getMobileProductImage(
    supersedeNo: string,
    uom: string
  ): Promise<{ DocName: string; path: string } | null> {
    const response = await apiClient.get<{ DocName: string; path: string } | null>(
      `/admin/products/${encodeURIComponent(supersedeNo)}/${encodeURIComponent(uom)}/mobile-image`
    );
    return response.data ?? null;
  }

  async deleteMobileProductImage(supersedeNo: string, uom: string): Promise<void> {
    await apiClient.delete(
      `/admin/products/${encodeURIComponent(supersedeNo)}/${encodeURIComponent(uom)}/mobile-image`
    );
  }

  /**
   * Replace the homepage hero image (issue #132). The backend saves the file
   * and repoints the homepage setting at it in one step.
   */
  async uploadHomepageImage(file: File): Promise<HomepageUploadResult> {
    return this.uploadHomepageFile('homepage-image', file, 'Homepage image upload failed');
  }

  /**
   * Put a photo behind the homepage hero (issue #140), framed on the focus
   * point and zoom chosen in the preview editor (issues #154, #162). All are
   * saved together.
   */
  async uploadHomepageBackground(file: File, position: string, zoom: number): Promise<HomepageUploadResult> {
    return this.uploadHomepageFile('homepage-background', file, 'Background photo upload failed', {
      position,
      zoom: String(zoom),
    });
  }

  /** Reframe the background photo already in place (issues #154, #162). */
  async updateHomepageBackgroundFraming(position: string, zoom: number): Promise<void> {
    await this.updateSettings({ homepage: { hero_bg_position: position, hero_bg_zoom: String(zoom) } });
  }

  /** Remove the photo behind the hero; its background colour shows again (issue #140). */
  async resetHomepageBackground(): Promise<void> {
    await apiClient.delete('/admin/settings/homepage-background');
  }

  private async uploadHomepageFile(
    endpoint: 'homepage-image' | 'homepage-background',
    file: File,
    fallbackMessage: string,
    fields: Record<string, string> = {}
  ): Promise<HomepageUploadResult> {
    const formData = new FormData();
    Object.entries(fields).forEach(([name, value]) => formData.append(name, value));
    formData.append('image', file);

    const authData = JSON.parse(localStorage.getItem('dasoonegg-auth') || '{}');
    const token = authData.state?.token;
    const baseUrl = import.meta.env.VITE_API_BASE_URL || '/api/v1';

    try {
      const response = await axios.post(`${baseUrl}/admin/settings/${endpoint}`, formData, {
        headers: { Authorization: token ? `Bearer ${token}` : '' },
        withCredentials: true,
      });
      return response.data.data;
    } catch (error) {
      // Show the backend's reason, not a bare "Request failed".
      if (axios.isAxiosError(error)) {
        // nginx's 413 page is HTML, so there is no backend message to show.
        if (error.response?.status === 413) {
          throw new Error(
            `The image is too large to upload. Please use a JPEG or PNG under ${HOMEPAGE_IMAGE_MAX_MB} MB.`
          );
        }
        const responseData = error.response?.data;
        throw new Error(
          responseData?.message
            || responseData?.error?.details
            || error.message
            || fallbackMessage
        );
      }
      throw error;
    }
  }

  /** Go back to the hero artwork that ships with the site (issue #132). */
  async resetHomepageImage(): Promise<void> {
    await apiClient.delete('/admin/settings/homepage-image');
  }

  async uploadMobileProductImage(
    supersedeNo: string,
    uom: string,
    file: File
  ): Promise<{
    DocName: string;
    path: string;
    width: number;
    height: number;
    size: number;
  }> {
    const formData = new FormData();
    formData.append('image', file);

    const authData = JSON.parse(localStorage.getItem('dasoonegg-auth') || '{}');
    const token = authData.state?.token;

    // Use axios directly for file upload to avoid Content-Type issues
    const baseUrl = import.meta.env.VITE_API_BASE_URL || '/api/v1';

    try {
      const response = await axios.post(
        `${baseUrl}/admin/products/${supersedeNo}/${uom}/mobile-image`,
        formData,
        {
          headers: {
            Authorization: token ? `Bearer ${token}` : '',
          },
          withCredentials: true,
        }
      );

      return response.data.data;
    } catch (error) {
      // Surface the backend's own message; a bare axios error says nothing
      // useful about why an upload was rejected.
      if (axios.isAxiosError(error)) {
        // nginx's 413 page is HTML, so there is no backend message to show.
        if (error.response?.status === 413) {
          throw new Error(
            `The image is too large to upload. Please use a JPEG or PNG under ${HOMEPAGE_IMAGE_MAX_MB} MB.`
          );
        }
        const responseData = error.response?.data;
        throw new Error(
          responseData?.message
            || responseData?.error?.details
            || error.message
            || 'Mobile image upload failed'
        );
      }
      throw error;
    }
  }

  // Category Management
  async getAllCategories(): Promise<ProductCategoryOption[]> {
    const response = await apiClient.get<ProductCategoryOption[]>('/admin/categories');
    return response.data!;
  }

  async createCategory(category: string, description: string): Promise<void> {
    await apiClient.post('/admin/categories', { Category: category, CategoryDescription: description });
  }

  async updateCategory(category: string, description: string): Promise<void> {
    await apiClient.put(`/admin/categories/${category}`, { CategoryDescription: description });
  }

  async deleteCategory(category: string): Promise<void> {
    await apiClient.delete(`/admin/categories/${category}`);
  }
}

export interface Product {
  SupersedeNoUOM: string;
  SupersedeNo: string;
  UOM: string;
  Description: string;
  Category?: string;
  CategoryDescription?: string;
  ProductDetail?: string;
  Price: number;
  PriceDesc1: string;
  PriceDesc2: string;
  ConvRate: number;
  NetContentLabel?: string;
  DeliveryCalUnit: number;
  MainPictureFull: string;
  MainPictureName: string;
  MobilePictureFull?: string;
  pathSupersede: string;
  Original: string;
  prodDetail?: string;
  DocName?: string;
  Active?: string; // 'Y' or 'N'
  Published?: boolean;
  WebSeq?: number | null;
}

export const adminService = new AdminService();
